import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Clock, CheckCircle2, CalendarDays, Flame, TrendingUp } from 'lucide-react';
import api from '../lib/api';
import { Card } from '../components/ui/Card';
import { TraderBadge } from '../components/TraderBadge';
import { Spinner } from '../components/ui/Spinner';

function Ring({ value, goal }) {
  const pct = goal > 0 ? Math.min(100, (value / goal) * 100) : 0;
  const r = 52;
  const c = 2 * Math.PI * r;
  return (
    <svg width="130" height="130" viewBox="0 0 130 130" className="-rotate-90">
      <circle cx="65" cy="65" r={r} fill="none" stroke="#27272a" strokeWidth="10" />
      <circle
        cx="65"
        cy="65"
        r={r}
        fill="none"
        stroke="#10B981"
        strokeWidth="10"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={c - (pct / 100) * c}
        style={{ transition: 'stroke-dashoffset 0.8s ease' }}
      />
    </svg>
  );
}

function Heatmap({ days }) {
  const map = {};
  (days || []).forEach((d) => (map[d.date] = d.minutes));
  const now = new Date();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 1; i <= daysInMonth; i++) {
    const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
    const mins = map[dateStr] || 0;
    let level = 0;
    if (mins > 0) level = 1;
    if (mins >= 20) level = 2;
    if (mins >= 45) level = 3;
    if (mins >= 90) level = 4;
    const shades = ['#1f1f22', '#0f5132', '#15803d', '#16a34a', '#22c55e'];
    cells.push(
      <div
        key={i}
        title={`${dateStr}: ${mins}m`}
        className="aspect-square rounded-[4px]"
        style={{ background: shades[level] }}
      />
    );
  }
  return <div className="grid grid-cols-7 gap-1.5 sm:grid-cols-[repeat(10,minmax(0,1fr))]">{cells}</div>;
}

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <Card className="p-5">
      <Icon size={18} className="text-emerald" />
      <div className="mt-3 tnum text-2xl font-bold text-white">{value}</div>
      <div className="text-xs text-zinc-500">{label}</div>
      {sub && <div className="mt-0.5 text-[11px] text-zinc-600">{sub}</div>}
    </Card>
  );
}

export default function Progress() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/progress-summary').then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, []);

  if (loading)
    return (
      <div className="grid h-[60vh] place-items-center">
        <Spinner className="h-8 w-8" />
      </div>
    );

  const cur = data.current_level || {};
  const next = data.next_level;

  return (
    <div className="space-y-8" data-testid="progress-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-white">Your Progress</h1>
        <p className="mt-1 text-sm text-zinc-500">Every hour counts toward Master tier.</p>
      </div>

      {/* Level hero */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <Card className="p-8">
          <div className="flex flex-col gap-8 md:flex-row md:items-center md:justify-between">
            <div className="space-y-4">
              <TraderBadge level={cur.level} name={cur.name} size="lg" />
              <div>
                <div className="font-heading text-lg text-white">{cur.title}</div>
                <p className="max-w-md text-sm text-zinc-400">{cur.description}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="tnum text-5xl font-extrabold text-white">{data.total_hours}</div>
              <div className="text-sm text-zinc-500">total hours invested</div>
              {next && (
                <div className="mt-3 tnum text-sm" style={{ color: next.color }}>
                  {data.hours_to_next_level}h to {next.name}
                </div>
              )}
            </div>
          </div>

          {next && (
            <div className="mt-6">
              <div className="h-2 w-full overflow-hidden rounded-full bg-white/10">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${Math.min(100, (1 - data.hours_to_next_level / Math.max(1, next.hours - cur.hours)) * 100)}%`,
                    background: cur.color,
                  }}
                />
              </div>
              <div className="mt-2 flex justify-between text-[11px] text-zinc-500">
                <span>{cur.name}</span>
                <span>{next.name}</span>
              </div>
            </div>
          )}
        </Card>
      </motion.div>

      {/* Daily goal + heatmap */}
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="flex flex-col items-center justify-center p-8">
          <div className="relative">
            <Ring value={data.today_minutes} goal={data.daily_goal_minutes} />
            <div className="absolute inset-0 grid place-items-center">
              <div className="text-center">
                <div className="tnum text-2xl font-bold text-white">{data.today_minutes}</div>
                <div className="text-[11px] text-zinc-500">/ {data.daily_goal_minutes}m today</div>
              </div>
            </div>
          </div>
          <div className="mt-4 text-sm text-zinc-400">Daily goal</div>
        </Card>

        <Card className="p-6 lg:col-span-2" data-testid="progress-heatmap">
          <div className="mb-4 flex items-center gap-2 text-sm text-zinc-400">
            <CalendarDays size={16} className="text-emerald" /> This month · {data.hours_this_month}h logged
          </div>
          <Heatmap days={data.calendar_month} />
        </Card>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat icon={CheckCircle2} label="Videos completed" value={data.videos_watched} />
        <Stat icon={Flame} label="Current streak" value={`${data.streak}d`} />
        <Stat icon={CalendarDays} label="Days practiced" value={data.days_practiced} />
        <Stat icon={TrendingUp} label="Weeks in a row" value={data.weeks_in_row} />
        <Stat icon={Clock} label="In-app hours" value={`${data.in_app_hours}h`} />
        <Stat icon={Clock} label="Outside hours" value={`${data.outside_hours}h`} sub="logged practice" />
      </div>

      {/* Level ladder */}
      <Card className="p-6" data-testid="tier-ladder">
        <h2 className="mb-4 font-heading text-lg font-semibold text-white">Tier ladder</h2>
        <div className="space-y-2">
          {(data.levels || []).map((lvl) => {
            const reached = data.total_hours >= lvl.hours;
            const isCurrent = lvl.level === cur.level;
            return (
              <div
                key={lvl.level}
                className="flex items-center gap-4 rounded-xl border px-4 py-3"
                style={{
                  borderColor: isCurrent ? `${lvl.color}66` : 'rgba(255,255,255,0.05)',
                  background: isCurrent ? `${lvl.color}12` : 'transparent',
                }}
              >
                <div
                  className="grid h-9 w-9 place-items-center rounded-lg font-heading font-extrabold"
                  style={{ color: lvl.color, background: `${lvl.color}1a`, opacity: reached ? 1 : 0.4 }}
                >
                  {lvl.level}
                </div>
                <div className="flex-1">
                  <div className="font-medium text-white" style={{ opacity: reached ? 1 : 0.6 }}>{lvl.name}</div>
                  <div className="text-xs text-zinc-500">{lvl.title}</div>
                </div>
                <div className="tnum text-sm text-zinc-500">{lvl.hours}h</div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
